import { FC, useState } from 'react';
import css from './emailBox.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import Popup from '@/components/SignUpForm/Popup';
import Mailchimp from '@/components/SignUpForm/Mailchimp';
import MailchimpView from '@/components/SignUpForm/Mailchimp/BaseFormView';
import Klaviyo from '@/components/SignUpForm/Klaviyo';
import KlaviyoView from '@/components/SignUpForm/Klaviyo/BaseFormView';
import { Upto1 } from '@/components/UI/svg/Wave2';
import PolicyCheck from '@/components/PolicyCheck';

const emailMap: any = {
    jp: {
        type: 'mailchimp',
        action: 'https://ecoflow.us8.list-manage.com/subscribe/post?u=d002bf04842356c4c1c5221cf&amp;id=440b6cbd62&amp;f_id=00b372e1f0',
        popupBtnLink: 'https://lin.ee/g7W0VbD',
    },
    kr: {
        type: 'mailchimp',
        action: 'https://ecoflow.us14.list-manage.com/subscribe/post?u=60f049f50357a87af9abdbbb2&amp;id=40eb7266fc&amp;f_id=00de40e0f0',
        popupBtnLink: 'https://lin.ee/g7W0VbD',
    },
    au: {
        type: 'klaviyo',
        list_id: 'Wmrg2y',
        source: 'EcoFlow Glacier and Wave 2 Teaser from',
        consent_form_id: 'Xai5nj',
        popupBtnLink: 'https://www.facebook.com/groups/efaustralia/',
    },
};

const EmailBox: FC = ({ checkedObj }: any) => {
    const { useLang, gtm, Link, useRouter, useEffect, useRef } = useGlobal();
    const { t } = useLang('glacierwave.treaser');
    const { locale } = useRouter();
    const [popupVisible, setPopupVisible] = useState(false);
    // @ts-ignore
    const emailConf = emailMap[locale] || emailMap.jp;
    const showPolicyCheck = ['au'].includes(locale);
    const [checked, setChecked] = useState(!showPolicyCheck);
    const extraProperties = useRef({
        custom_source: emailConf.source,
        $source: emailConf.source,
        $method_type: 'Klaviyo Form',
        $consent_version: 'Embed default text',
        $consent_form_id: emailConf.consent_form_id,
        glacierWave2: emailConf.glacierWave2 || '',
    });
    const [canSign, setCanSign] = useState(checked);

    const getDataLayer = (type: string) => {
        return {
            event: 'uaEvent',
            event_name: 'EcoFlow GLACIER & WAVE2',
            product_name: 'EcoFlow GLACIER & WAVE2',
            button_name: type,
        };
    };

    const onCheckChange = (e: any) => {
        setChecked(e.target.checked);
    };

    useEffect(() => {
        let temp = '';
        if (checkedObj?.wave2) temp = 'Wave2';
        if (checkedObj?.glacier) temp += `${checkedObj?.wave2 ? ', ' : ''}Glacier`;
        extraProperties.current.glacierWave2 = temp;
        extraProperties.current.$source = emailConf.source || '';
        extraProperties.current.$consent_form_id = emailConf.consent_form_id || '';
        extraProperties.current.custom_source = emailConf.source || '';
        setCanSign(checked && (['au'].includes(locale) ? extraProperties.current.glacierWave2 : true));
    }, [emailConf, checkedObj, locale, checked]);

    return (
        <div className={css.emailBox}>
            <div className="email-box-wrapper">
                <div className="email-desc">
                    {t.rich('email_description', {
                        link: (text: string) => (
                            <Link target="_blank" href="https://lin.ee/g7W0VbD" dataLayer={getDataLayer(text)}>
                                <span className="blue-text">{text}</span>
                            </Link>
                        ),
                        white: (text: string) => <span className="white">{text}</span>,
                        small: (text: string) => <span className="small">{text}</span>,
                    })}
                </div>
                {emailConf?.type === 'mailchimp' && (
                    <Mailchimp
                        successCallback={() => {
                            setPopupVisible(true);
                            try {
                                gtm.push({
                                    event: 'uaEvent',
                                    event_name: 'activity_submit',
                                    button_name: t('email_subscribe'),
                                    product_name: 'EcoFlow GLACIER & WAVE2',
                                    status: 'success',
                                });
                                // @ts-ignore
                                window?.fbq('track', 'Lead');
                            } catch (e) {
                                console.error(e);
                            }
                        }}
                        errorCallback={() => {
                            gtm.push({
                                event: 'uaEvent',
                                event_name: 'activity_submit',
                                button_name: t('email_subscribe'),
                                product_name: 'EcoFlow GLACIER & WAVE2',
                                status: 'fail',
                            });
                        }}
                    >
                        <MailchimpView
                            action={emailConf.action}
                            extraFields={emailConf.extraFields}
                            submitText={t('email_subscribe')}
                            placeholder={t('email_input_placeholder')}
                            disabled={!checked}
                        />
                    </Mailchimp>
                )}
                {emailConf?.type === 'klaviyo' && (
                    <Klaviyo
                        extra_properties={extraProperties.current}
                        successCallback={() => {
                            setPopupVisible(true);
                            try {
                                gtm.push({
                                    event: 'uaEvent',
                                    event_name: 'activity_submit',
                                    button_name: t('email_subscribe'),
                                    product_name: 'EcoFlow GLACIER & WAVE2',
                                    status: 'success',
                                });
                                // @ts-ignore
                                window?.fbq('track', 'Lead');
                            } catch (e) {
                                console.error(e);
                            }
                        }}
                        errorCallback={() => {
                            gtm.push({
                                event: 'uaEvent',
                                event_name: 'activity_submit',
                                button_name: t('email_subscribe'),
                                product_name: 'EcoFlow GLACIER & WAVE2',
                                status: 'fail',
                            });
                        }}
                    >
                        <KlaviyoView
                            list_id={emailConf.list_id}
                            submitText={t('email_subscribe')}
                            placeholder={t('email_input_placeholder')}
                            disabled={!canSign}
                        />
                    </Klaviyo>
                )}
                {showPolicyCheck && (
                    <PolicyCheck
                        terms={locale === 'au' ? 'https://au.ecoflow.com/pages/terms-of-use-1' : ''}
                        policy={locale === 'au' ? 'https://au.ecoflow.com/pages/privacy-policy' : ''}
                        onCheckChange={onCheckChange}
                    />
                )}
                <div className="icon">
                    <Upto1 off="30" text="最大" textX={13} />
                </div>
                <Popup
                    className={css.popupModal}
                    visible={popupVisible}
                    title={t('popup_title')}
                    content={showPolicyCheck ? t('popup_text') : ''}
                    // content={''}
                    buttonText={t('popup_btn')}
                    showButton={showPolicyCheck}
                    onCancel={() => setPopupVisible(false)}
                    btnLink={emailConf.popupBtnLink}
                    theme={'black'}
                />
            </div>
        </div>
    );
};

export default EmailBox;
